import { motion } from "framer-motion";
import { Cloud, Heart, Laptop, Music, Play, Radio } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { MediaMenu } from "./MediaMenu";

export interface MediaItemProps {
  id: string;
  name: string;
  artist: string;
  coverUrl?: string;
  trackCount: number;
  source: "spotify" | "local";
  type: "album" | "playlist" | "radio" | "artist" | "track";
  isLiked?: boolean;
  onPlay?: () => void;
  onLike?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const getLinkPath = (type: string, id: string) => {
  switch (type) {
    case "radio":
      return `/radio/${id}`;
    case "artist":
      return `/artist/${id}`;
    case "track":
      return `/tracks/${id}`;
    default:
      return `/${type}s/${id}`;
  }
};

export const MediaGrid: React.FC<{ items: MediaItemProps[] }> = ({ items }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {items.map((item) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="group relative bg-zinc-800/50 p-4 rounded-lg hover:bg-zinc-800 transition"
        >
          <Link to={getLinkPath(item.type, item.id)} className="block">
            <div className="relative">
              {item.coverUrl ? (
                <img
                  src={item.coverUrl}
                  alt={item.name}
                  className="w-full aspect-square object-cover rounded-md mb-4"
                />
              ) : (
                <div className="w-full aspect-square bg-zinc-800 rounded-md mb-4 flex items-center justify-center">
                  {item.type === "radio" ? (
                    <Radio className="w-12 h-12 text-zinc-600" />
                  ) : (
                    <Music className="w-12 h-12 text-zinc-600" />
                  )}
                </div>
              )}

              <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60">
                {item.source === "spotify" ? <Cloud className="w-3 h-3" /> : <Laptop className="w-3 h-3" />}
              </div>

              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    item.onPlay?.();
                  }}
                  className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center transform translate-y-4 group-hover:translate-y-0 transition-transform"
                >
                  <Play className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <h3 className="font-medium truncate">{item.name}</h3>
              <p className="text-sm text-zinc-400 truncate">{item.artist}</p>
              {item.type !== "artist" && item.type !== "track" && (
                <p className="text-xs text-zinc-500">
                  {item.trackCount}{" "}
                  {item.type === "radio" ? "songs in rotation" : `track${item.trackCount !== 1 ? "s" : ""}`}
                </p>
              )}
            </div>
          </Link>

          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
            {item.onLike && (
              <button
                onClick={item.onLike}
                className={`p-2 rounded-full bg-black/60 ${
                  item.isLiked ? "text-yellow-500" : "text-white"
                } hover:scale-110 transition`}
              >
                <Heart className="w-4 h-4" />
              </button>
            )}

            {false && <MediaMenu type={item.type} onEdit={item.onEdit} onDelete={item.onDelete} />}
          </div>
        </motion.div>
      ))}
    </div>
  );
};
